/* <bg-video> — autoplaying muted background video, no third-party JS on load.

   Replaces the Alpine `ThemeModule_BackgroundVideo` pattern (ds_plan Part 7, pattern 2).
   That module lived in assets/theme.min.js, which only layout/gift_card.liquid ever
   renders, so `x-init="mounted()"` never ran on a normal page and the YouTube branch
   rendered an empty <div id="player-…">. Measured on /pages/birthstone-capsule: live 1
   iframe, dev 0 — an empty 1080px box, with no fallback image behind it on that instance.

   Two failures had to be fixed, not one. assets/background-video.min.js *did* load (the
   snippet rendered its own script tag) and threw `ReferenceError: defineGlobalOnce is not
   defined` on every page load. That helper came from `common.js`, which is absent from the
   repo entirely — so restoring Alpine alone would NOT have fixed these videos.

   Deliberately does NOT use the YouTube IFrame API. A plain embed with autoplay/mute/loop
   gives the same result without loading YouTube's ~100KB player JS, and the iframe is only
   created once the element scrolls into view.

   Expected markup:
     <bg-video data-video-id="abc123"> …fallback <img> as real markup… </bg-video>
*/
class BgVideo extends HTMLElement {
  connectedCallback() {
    this.id_ = this.dataset.videoId;
    if (!this.id_) return;

    // Respect the user's motion preference: leave the fallback image in place.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    if (!('IntersectionObserver' in window)) return this.mount();

    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          this.observer.disconnect();
          this.mount();
        }
      },
      { rootMargin: '200px' }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  mount() {
    if (this.dataset.mounted) return;
    this.dataset.mounted = 'true';

    const p = new URLSearchParams({
      autoplay: '1',
      mute: '1',
      loop: '1',
      playlist: this.id_, // loop=1 is ignored on a single video without this
      controls: '0',
      modestbranding: '1',
      rel: '0',
      playsinline: '1',
      disablekb: '1',
    });

    const iframe = document.createElement('iframe');
    iframe.src = `https://www.youtube.com/embed/${encodeURIComponent(this.id_)}?${p}`;
    iframe.className = 'absolute top-0 left-0 right-0 bottom-0 w-full h-full';
    iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture');
    iframe.setAttribute('title', this.dataset.title || '');
    iframe.setAttribute('tabindex', '-1'); // decorative: keep it out of the tab order
    iframe.setAttribute('aria-hidden', 'true');
    iframe.setAttribute('loading', 'lazy');
    this.appendChild(iframe);
  }
}

if (!customElements.get('bg-video')) {
  customElements.define('bg-video', BgVideo);
}
